import {
  addDays,
  addMonths,
  differenceInDays,
  differenceInMonths,
  format,
  getDate,
  getMonth,
  getYear,
  isValid,
  parse,
  parseISO,
  subDays,
  subMonths
} from 'date-fns'
import * as jsepModule from 'jsep'

/**
 * ☠️ `jsep` es un módulo CommonJS que exporta la función DIRECTO, sin `default`.
 *
 * Este paquete lo compilan dos configuraciones distintas —la web con `esModuleInterop`, la API sin
 * él— y con la segunda un `import jsep from 'jsep'` emite `jsep_1.default`, que es `undefined`. No
 * rompe el typecheck ni la web: rompe en RUNTIME, y como el evaluador atrapa sus propios errores,
 * el síntoma es que TODA fórmula devuelve vacío en silencio. Se resuelve leyendo las dos formas.
 */
type JsepParse = (expression: string) => any
const jsep: JsepParse =
  (jsepModule as unknown as { default?: JsepParse }).default ?? (jsepModule as unknown as JsepParse)

import type { Collections, TypeOf } from './types'

/**
 * ⛔ ESTE ARCHIVO LO CORREN LOS DOS LADOS: la vista previa del editor y el envío del correo.
 *
 * Estaba duplicado —21 funciones en la web, 6 escritas aparte en el backend— y el resultado medido
 * fue una fórmula que se veía bien en la previsualización y salía VACÍA en el correo real, sin un
 * error en ningún lado. Agregar una función acá la agrega en las dos puntas o en ninguna.
 *
 * Evaluador de fórmulas estilo Notion. jsep parsea la expresión a un AST y
 * nosotros lo caminamos con una LISTA BLANCA de operadores y funciones (sin
 * `eval`) → seguro por diseño. Soporta números, booleanos, FECHAS (date-fns) y
 * TEXTO. Acceso a campos ANIDADOS (`cliente.nombre`) como lookup de clave
 * punteada contra el mapa de variables (sin acceso real a objetos/prototipos).
 * Funciones con nombres en español.
 */

type Value = number | boolean | Date | string

interface EvalCtx {
  vars: Record<string, string>
  collections?: Collections
  typeOf?: TypeOf
}

const isDate = (v: Value): v is Date => v instanceof Date

// A número: fecha→timestamp, string→parseFloat (vacío/no numérico→0), bool→0/1.
const toNum = (v: Value): number => {
  if (isDate(v)) return v.getTime()
  if (typeof v === 'boolean') return v ? 1 : 0
  if (typeof v === 'string') {
    const n = parseFloat(v.replace(/,/g, ''))
    return isNaN(n) ? 0 : n
  }
  return v
}

const FALSY_WORDS = new Set(['', '0', 'no', 'false', 'falso'])
const toBool = (v: Value): boolean => {
  if (isDate(v)) return true
  if (typeof v === 'boolean') return v
  if (typeof v === 'string') return !FALSY_WORDS.has(v.trim().toLowerCase())
  return v !== 0
}

// Texto crudo de un valor (para comparaciones/serialización de strings).
const toText = (v: Value): string => {
  if (isDate(v)) return format(v, 'yyyy-MM-dd')
  if (typeof v === 'boolean') return v ? '1' : '0'
  return String(v)
}

// ¿El valor representa un número (incluye fechas y strings numéricos)?
const looksNumeric = (v: Value): boolean => {
  if (typeof v === 'number' || typeof v === 'boolean' || isDate(v)) return true
  if (typeof v === 'string') {
    const t = v.trim()
    return t !== '' && !isNaN(Number(t.replace(/,/g, '')))
  }
  return false
}

// Parseo flexible de fechas (ISO, dd/MM/yyyy, o nativo).
export const parseDateStr = (s: string): Date | null => {
  if (!s) return null
  let d = parseISO(s)
  if (isValid(d)) return d
  d = parse(s, 'dd/MM/yyyy', new Date())
  if (isValid(d)) return d
  d = new Date(s)
  return isValid(d) ? d : null
}

const asDate = (v: Value): Date => {
  if (isDate(v)) return v
  if (typeof v === 'string') {
    const d = parseDateStr(v)
    if (d) return d
  }
  throw new Error('Se esperaba una fecha')
}

// Funciones disponibles (nombre en español → implementación).
const FUNCS: Record<string, (...args: Value[]) => Value> = {
  // ── Numéricas ──
  suma: (...a) => a.reduce<number>((x, y) => x + toNum(y), 0),
  redondear: (n, dec) => {
    const f = 10 ** (dec !== undefined ? toNum(dec) : 0)
    return Math.round(toNum(n) * f) / f
  },
  absoluto: (n) => Math.abs(toNum(n)),
  minimo: (...a) => Math.min(...a.map(toNum)),
  maximo: (...a) => Math.max(...a.map(toNum)),
  techo: (n) => Math.ceil(toNum(n)),
  piso: (n) => Math.floor(toNum(n)),
  potencia: (b, e) => toNum(b) ** toNum(e),
  raiz: (n) => Math.sqrt(toNum(n)),
  residuo: (a, b) => toNum(a) % toNum(b),
  si: (cond, a, b) => (toBool(cond) ? a : b),
  // ── Fechas ──
  meses_entre: (a, b) => differenceInMonths(asDate(a), asDate(b)),
  dias_entre: (a, b) => differenceInDays(asDate(a), asDate(b)),
  restar_meses: (d, n) => subMonths(asDate(d), toNum(n)),
  sumar_meses: (d, n) => addMonths(asDate(d), toNum(n)),
  restar_dias: (d, n) => subDays(asDate(d), toNum(n)),
  sumar_dias: (d, n) => addDays(asDate(d), toNum(n)),
  anio: (d) => getYear(asDate(d)),
  mes: (d) => getMonth(asDate(d)) + 1,
  dia: (d) => getDate(asDate(d)),
  hoy: () => new Date()
}

// Nombres de función (para el resaltado de sintaxis). Incluye `unir` (forma especial).
export const FUNCTION_NAMES: string[] = [...Object.keys(FUNCS), 'unir']

// Para la ayuda del editor (con descripción + ejemplo, estilo Notion/Coda).
export const FORMULA_FUNCTIONS: Array<{ name: string; desc: string; example?: string }> = [
  {
    name: 'suma(a, b, …)',
    desc: 'Suma todos los valores indicados.',
    example: 'suma(reserva, total_cuotas)'
  },
  {
    name: 'redondear(n, dec)',
    desc: 'Redondea un número a la cantidad de decimales indicada.',
    example: 'redondear(valor_total / 12, 2)'
  },
  {
    name: 'absoluto(n)',
    desc: 'Devuelve el valor absoluto (sin signo).',
    example: 'absoluto(-150) → 150'
  },
  {
    name: 'minimo(a, b, …)',
    desc: 'Devuelve el menor de los valores.',
    example: 'minimo(reserva, total_cuotas)'
  },
  {
    name: 'maximo(a, b, …)',
    desc: 'Devuelve el mayor de los valores.',
    example: 'maximo(reserva, total_cuotas)'
  },
  { name: 'residuo(a, b)', desc: 'El resto de dividir a entre b.', example: 'residuo(10, 3) → 1' },
  {
    name: 'si(cond, a, b)',
    desc: 'Devuelve a si la condición es verdadera; si no, b.',
    example: 'si(reserva > 0, reserva, valor_total)'
  },
  {
    name: 'meses_entre(a, b)',
    desc: 'Cantidad de meses entre dos fechas.',
    example: 'meses_entre(fecha_ultima_cuota, fecha_primera_cuota)'
  },
  {
    name: 'dias_entre(a, b)',
    desc: 'Cantidad de días entre dos fechas.',
    example: 'dias_entre(fecha_entrega, fecha_de_hoy)'
  },
  {
    name: 'restar_meses(fecha, n)',
    desc: 'Resta n meses a una fecha.',
    example: 'restar_meses(fecha_ultima_cuota, 24)'
  },
  {
    name: 'sumar_meses(fecha, n)',
    desc: 'Suma n meses a una fecha.',
    example: 'sumar_meses(fecha_entrega, 6)'
  },
  { name: 'anio(fecha)', desc: 'El año de una fecha.', example: 'anio(fecha_entrega) → 2027' },
  { name: 'hoy()', desc: 'La fecha de hoy.', example: 'dias_entre(fecha_entrega, hoy())' },
  {
    name: 'unir(a, b, …)',
    desc: 'Une varios valores en una lista (… , … y …). Cada argumento es un valor; si es una colección.campo (ej. clientes.nombre) se expande a todos los items.',
    example: 'unir(representante.nombre, clientes.nombre) → "HERBERT, JUAN y PEDRO"'
  }
]

const COMPARATORS = ['==', '!=', '<', '<=', '>', '>=']

// Reconstruye la ruta punteada de un MemberExpression (a.b.c). Solo identificadores
// estáticos; sin acceso computado/dinámico → sin superficie de inyección.
const memberPath = (node: any): string => {
  if (node?.type === 'Identifier') return node.name
  if (node?.type === 'MemberExpression' && !node.computed && node.property?.type === 'Identifier') {
    return `${memberPath(node.object)}.${node.property.name}`
  }
  throw new Error('Acceso no permitido')
}

const FORBIDDEN = ['__proto__', 'prototype', 'constructor']

// Resuelve un nombre/ruta a un Value leyendo el mapa de variables.
const resolveName = (name: string, ctx: EvalCtx): Value => {
  const raw = ctx.vars[name]
  if (raw === undefined) throw new Error(`Variable desconocida: ${name}`)
  const type = ctx.typeOf?.(name)
  if (type === 'date') {
    const d = parseDateStr(String(raw))
    if (!d) throw new Error(`Fecha inválida: ${name}`)
    return d
  }
  if (type === 'number') {
    const n = parseFloat(String(raw).replace(/,/g, ''))
    return isNaN(n) ? 0 : n
  }
  // String/enum/desconocido (incluye campos de colección) → texto crudo.
  return String(raw)
}

const walk = (node: any, ctx: EvalCtx): Value => {
  switch (node?.type) {
    case 'Literal':
      if (typeof node.value === 'boolean') return node.value
      if (typeof node.value === 'string') return node.value
      return Number(node.value)

    case 'Identifier':
      return resolveName(node.name, ctx)

    case 'MemberExpression': {
      if (node.computed) throw new Error('Acceso dinámico no permitido')
      const path = memberPath(node)
      if (FORBIDDEN.some((f) => path.includes(f))) throw new Error('Acceso no permitido')
      return resolveName(path, ctx)
    }

    case 'UnaryExpression': {
      const a = walk(node.argument, ctx)
      if (node.operator === '-') return -toNum(a)
      if (node.operator === '+') return toNum(a)
      if (node.operator === '!') return !toBool(a)
      throw new Error(`Operador no permitido: ${node.operator}`)
    }

    case 'BinaryExpression': {
      const l = walk(node.left, ctx)
      const r = walk(node.right, ctx)
      const op = node.operator
      // Igualdad "inteligente": si ambos lados son numéricos → numérica; si no → texto.
      if (op === '==' || op === '!=') {
        const eq =
          looksNumeric(l) && looksNumeric(r)
            ? toNum(l) === toNum(r)
            : toText(l).trim() === toText(r).trim()
        return op === '==' ? eq : !eq
      }
      const a = toNum(l)
      const b = toNum(r)
      if (COMPARATORS.includes(op)) {
        switch (op) {
          case '<':
            return a < b
          case '<=':
            return a <= b
          case '>':
            return a > b
          case '>=':
            return a >= b
        }
      }
      switch (op) {
        case '+':
          return a + b
        case '-':
          return a - b
        case '*':
          return a * b
        case '/':
          return a / b
        case '%':
          return a % b
        default:
          throw new Error(`Operador no permitido: ${op}`)
      }
    }

    case 'ConditionalExpression':
      return toBool(walk(node.test, ctx)) ? walk(node.consequent, ctx) : walk(node.alternate, ctx)

    case 'LogicalExpression': {
      // && y || con corto-circuito (la derecha solo se evalúa si hace falta).
      if (node.operator === '&&')
        return toBool(walk(node.left, ctx)) && toBool(walk(node.right, ctx))
      if (node.operator === '||')
        return toBool(walk(node.left, ctx)) || toBool(walk(node.right, ctx))
      throw new Error(`Operador lógico no permitido: ${node.operator}`)
    }

    case 'CallExpression': {
      const name = node.callee?.name as string | undefined
      // `unir(coleccion, campo, "y"|"o")` — forma especial: NO se caminan los args,
      // se leen como nombres + acceden a las colecciones.
      if (name === 'unir') {
        // Variádico, como suma(): cada argumento es un valor. Si es una referencia
        // `coleccion.campo` (la raíz es una colección conocida), se EXPANDE a todos
        // los items; si no, se resuelve como valor escalar. Todo se une con "y".
        const out: string[] = []
        for (const arg of node.arguments ?? []) {
          if (arg?.type === 'MemberExpression' && !arg.computed) {
            const path = memberPath(arg)
            const dot = path.indexOf('.')
            const root = dot >= 0 ? path.slice(0, dot) : path
            const field = dot >= 0 ? path.slice(dot + 1) : ''
            const list = ctx.collections?.[root]
            if (list) {
              for (const it of list) {
                const val = field ? it?.[field] : undefined
                if (val) out.push(String(val))
              }
              continue
            }
          }
          const val = toText(walk(arg, ctx))
          if (val) out.push(val)
        }
        return new Intl.ListFormat('es', { style: 'long', type: 'conjunction' }).format(out)
      }
      const fn = name ? FUNCS[name] : undefined
      if (!fn) throw new Error(`Función desconocida: ${name ?? '?'}`)
      const args = (node.arguments ?? []).map((arg: any) => walk(arg, ctx))
      return fn(...args)
    }

    default:
      throw new Error('Expresión no permitida')
  }
}

/**
 * Los TOKENS que una fórmula referencia: `suma(etapa4.monto, 2)` → `['etapa4.monto']`.
 *
 * Lo usa el publish para validar el linaje de las variables escritas dentro de una fórmula. Se
 * parsea con el MISMO jsep que evalúa: un regex propio acá sería un segundo parser, y el día que
 * uno acepte algo que el otro no, el editor deja publicar un correo que después no resuelve.
 *
 * No devuelve los nombres de función: el `callee` de una llamada se saltea a propósito.
 */
export const formulaVariables = (expr: string): string[] => {
  if (!expr || !expr.trim()) return []
  const out = new Set<string>()
  const visit = (node: any): void => {
    if (!node || typeof node !== 'object') return
    if (node.type === 'Identifier') {
      out.add(node.name)
      return
    }
    if (node.type === 'MemberExpression') {
      if (node.computed) {
        visit(node.object)
        visit(node.property)
        return
      }
      try {
        out.add(memberPath(node))
      } catch {
        visit(node.object)
      }
      return
    }
    if (node.type === 'CallExpression') {
      // El nombre de la función NO es una variable; sus argumentos sí pueden serlo.
      for (const argument of node.arguments ?? []) visit(argument)
      return
    }
    for (const key of ['left', 'right', 'argument', 'test', 'consequent', 'alternate']) {
      if (node[key]) visit(node[key])
    }
  }
  try {
    visit(jsep(expr))
  } catch {
    // Una fórmula que no parsea no referencia nada que se pueda validar. El editor la marca en rojo.
    return []
  }
  return [...out]
}

export type FormulaKind = 'number' | 'date' | 'boolean' | 'text' | null

export interface FormulaResult {
  // Valor ya serializado a string: número decimal, fecha ISO, '1'/'0', o texto.
  value: string | null
  kind: FormulaKind
  error?: string
}

/**
 * Evalúa una fórmula contra el mapa de variables (y opcionalmente las colecciones).
 * Devuelve el valor serializado o un error legible. La máscara final se aplica
 * fuera (según el `kind` y lo que elija el redactor).
 */
export const evaluateFormula = (
  expr: string,
  variables: Record<string, string>,
  collections?: Collections,
  typeOf?: TypeOf
): FormulaResult => {
  if (!expr || !expr.trim()) return { value: null, kind: null }
  try {
    const r = walk(jsep(expr), { vars: variables, collections, typeOf })
    if (isDate(r)) {
      if (!isValid(r)) return { value: null, kind: null, error: 'Fecha inválida' }
      return { value: format(r, 'yyyy-MM-dd'), kind: 'date' }
    }
    if (typeof r === 'boolean') return { value: r ? '1' : '0', kind: 'boolean' }
    if (typeof r === 'string') return { value: r, kind: 'text' }
    if (typeof r !== 'number' || isNaN(r) || !isFinite(r)) {
      return { value: null, kind: null, error: 'Resultado inválido' }
    }
    return { value: String(Math.round(r * 100) / 100), kind: 'number' }
  } catch (e: any) {
    return { value: null, kind: null, error: e?.message ?? 'Error en la fórmula' }
  }
}

// Evalúa una expresión como condición booleana (para el condicional "Expresión").
export const evaluateFormulaBoolean = (
  expr: string,
  variables: Record<string, string>,
  collections?: Collections,
  typeOf?: TypeOf
): boolean => {
  const res = evaluateFormula(expr, variables, collections, typeOf)
  if (res.value === null) return false
  if (res.kind === 'boolean') return res.value === '1'
  if (res.kind === 'number') return Number(res.value) !== 0
  if (res.kind === 'text') return !FALSY_WORDS.has(res.value.trim().toLowerCase())
  return true // una fecha válida cuenta como verdadero
}
